import { useState, useEffect, useCallback, useMemo } from "react";
import { CloudUpload, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useParams } from "@tanstack/react-router";
import { SideSheet, Button } from "@/components/ui";
import { UploadDropZone } from "./UploadDropZone";
import { UploadFileRow } from "./UploadFileRow";
import { UploadProcessingView } from "./UploadProcessingView";
import { CategoryAssignmentView } from "./CategoryAssignmentView";
import { useUploadDocuments } from "@/hooks/use-upload-documents";
import type { ConflictResolution } from "@/hooks/use-upload-documents";
import { FileIcon } from "../FileIcon";
import { DuplicateConflictModal } from "./DuplicateConflictModal";
import type { DocumentView } from "../types";
import { useUploadSessionStore } from "@/stores/upload-session-store";
import type { Id } from "@/packages/types";
import { AuditContextBadge } from "./AuditContextBadge";
import { AuditMultiSelect } from "./AuditMultiSelect";

interface UploadSheetProps {
  open: boolean;
  onClose: () => void;
  recentDocuments?: DocumentView[];
  /** When set, the upload sheet is in audit-view mode (read-only badge, scoped to this audit). */
  preSelectedAudit?: {
    id: Id<"audits">;
    displayName: string;
    code: string;
  };
}

export function UploadSheet({
  open,
  onClose,
  recentDocuments = [],
  preSelectedAudit,
}: UploadSheetProps) {
  const {
    files,
    phase,
    hasInteracted,
    maxFiles,
    canAddMore,
    addFiles,
    removeFile,
    renameFile,
    addTag,
    removeTag,
    setFileCategoryId,
    setFileExpirationDate,
    reset,
    startUpload,
    allFilesHaveCategory,
    filesWithCategory,
    conflicts,
    resolveConflicts,
    nextAvailableNames,
  } = useUploadDocuments();

  const [showConflictModal, setShowConflictModal] = useState(false);
  const [resolvedConflictNames, setResolvedConflictNames] = useState<
    Set<string>
  >(new Set());
  const [fileValidationErrors, setFileValidationErrors] = useState<
    Map<string, string>
  >(new Map());

  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  const store = useUploadSessionStore();

  // Sync store when opening in audit-view mode so the mutation receives auditContextIds
  // biome-ignore lint/correctness/useExhaustiveDependencies: preSelectedAudit is recreated on each parent render, only the ID matters
  useEffect(() => {
    const auditId = preSelectedAudit?.id;
    if (open && auditId) {
      store.setSelectedAuditIds([auditId]);
    }
  }, [open, preSelectedAudit?.id]);

  const unresolvedConflicts = useMemo(
    () => conflicts.filter((c) => !resolvedConflictNames.has(c.fileName)),
    [conflicts, resolvedConflictNames],
  );

  // Auto-open conflict modal as soon as unresolved duplicates are detected (document-007)
  useEffect(() => {
    if (unresolvedConflicts.length > 0 && !showConflictModal) {
      setShowConflictModal(true);
    }
  }, [unresolvedConflicts.length, showConflictModal]);

  const handleClose = () => {
    // When processing or completed, just close the sheet -- preserve session state
    if (phase === "processing" || phase === "completed") {
      onClose();
      return;
    }
    setResolvedConflictNames(new Set());
    setFileValidationErrors(new Map());
    store.setSelectedAuditIds([]);
    store.setExplicitNoAudit(false);
    reset();
    onClose();
  };

  // When a file is renamed, clear its resolved-conflict cache so the duplicate
  // check re-evaluates -- prevents renaming back to the original conflicting name.
  // Also clear any inline validation error for this file.
  const handleRenameFile = useCallback(
    (id: string, newName: string) => {
      const file = files.find((f) => f.id === id);
      if (file && resolvedConflictNames.has(file.originalName)) {
        setResolvedConflictNames((prev) => {
          const next = new Set(prev);
          next.delete(file.originalName);
          return next;
        });
      }
      setFileValidationErrors((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      renameFile(id, newName);
    },
    [files, resolvedConflictNames, renameFile],
  );

  const handleFilesSelected = (newFiles: File[]) => {
    addFiles(newFiles);
  };

  const handleUploadMore = () => {
    reset();
  };

  const handleRecentDocumentClick = useCallback(
    (docId: string) => {
      // Recent uploads are only visible before any interaction (idle state),
      // so a simple reset + close is sufficient
      reset();
      onClose();
      navigate({
        to: "/$workspaceSlug/documents/$documentId",
        params: { workspaceSlug: workspaceSlug ?? "", documentId: docId },
      });
    },
    [reset, onClose, navigate, workspaceSlug],
  );

  const goToAssigning = () => {
    store.setPhase("assigning");
  };

  const handleContinue = () => {
    // In documents-list mode, require explicit audit selection before proceeding
    if (!preSelectedAudit) {
      const hasAuditSelection =
        store.selectedAuditIds.length > 0 || store.explicitNoAudit;
      if (!hasAuditSelection) {
        return;
      }
    }

    // Validate all filenames before proceeding
    const errors = new Map<string, string>();
    for (const file of files) {
      if (!file.displayName.trim()) {
        errors.set(file.id, "Filename cannot be empty");
        continue;
      }
      const isConflicting =
        conflicts.some((c) => c.fileName === file.displayName) &&
        !resolvedConflictNames.has(file.displayName);
      if (isConflicting) {
        errors.set(file.id, "A document with this name already exists");
      }
    }
    if (errors.size > 0) {
      setFileValidationErrors(errors);
      return;
    }
    setFileValidationErrors(new Map());

    goToAssigning();
  };

  const handleConflictsResolved = (
    resolutions: Map<string, ConflictResolution>,
  ) => {
    resolveConflicts(resolutions);
    setResolvedConflictNames((prev) => {
      const next = new Set(prev);
      for (const key of resolutions.keys()) next.add(key);
      return next;
    });
    setShowConflictModal(false);
  };

  const handleConflictCancel = () => {
    setShowConflictModal(false);
  };

  const goBackToFileList = () => {
    store.setPhase("selecting");
  };

  // Set of file names that have duplicate conflicts (for per-row indicators)
  const duplicateFileNames = useMemo(
    () => new Set(conflicts.map((c) => c.fileName)),
    [conflicts],
  );

  const hasFiles = files.length > 0;
  const isUploading = phase === "uploading";
  const isProcessing = phase === "processing";
  const isCompleted = phase === "completed";
  const isAssigning = store.phase === "assigning";
  const showUploadForm = phase === "idle" || store.phase === "selecting";
  const showRecentUploads = !hasInteracted && recentDocuments.length > 0;

  const showProcessingView = isProcessing || isCompleted;

  // Processing/completed view -- shows real-time processing status per file
  if (showProcessingView) {
    return (
      <SideSheet open={open} onClose={handleClose}>
        <SideSheet.Header
          title="Upload Documents"
          icon={<CloudUpload size={18} />}
        />
        <SideSheet.Body>
          <AnimatePresence mode="wait">
            <motion.div
              key="processing-view"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              <UploadProcessingView />
            </motion.div>
          </AnimatePresence>
        </SideSheet.Body>
        <SideSheet.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleUploadMore}>
            Upload more
          </Button>
        </SideSheet.Footer>
      </SideSheet>
    );
  }

  return (
    <>
      <SideSheet open={open} onClose={handleClose}>
        <SideSheet.Header
          title="Upload Documents"
          icon={<CloudUpload size={18} />}
        />
        <SideSheet.Body>
          {/* Audit context: badge (audit-view) or multi-select (documents-list) */}
          {preSelectedAudit ? (
            <AuditContextBadge audit={preSelectedAudit} />
          ) : (
            <AuditMultiSelect
              value={store.selectedAuditIds}
              onChange={store.setSelectedAuditIds}
              explicitNoAudit={store.explicitNoAudit}
              onNoAuditChange={store.setExplicitNoAudit}
            />
          )}

          {/* Drop zone: expanded when no files, collapsed when files exist */}
          {showUploadForm && (
            <UploadDropZone
              collapsed={hasFiles}
              onFilesSelected={handleFilesSelected}
              disabled={!canAddMore || isUploading}
            />
          )}

          {/* Uploading state -- show drop zone collapsed */}
          {isUploading && (
            <UploadDropZone
              collapsed={true}
              onFilesSelected={handleFilesSelected}
              disabled={true}
            />
          )}

          {/* File count indicator */}
          {hasFiles && (
            <div className="flex items-center justify-between mt-4 mb-2 px-1">
              <span className="text-xs font-medium text-secondary">
                Selected files
              </span>
              <span className="text-xs text-muted">
                {files.length} / {maxFiles} files
              </span>
            </div>
          )}

          {/* File list with inline tag input - hidden in assigning phase */}
          {hasFiles && !isAssigning && (
            <div className="space-y-2">
              <AnimatePresence mode="popLayout">
                {files.map((file) => (
                  <UploadFileRow
                    key={file.id}
                    file={file}
                    onRename={handleRenameFile}
                    onRemove={(id) => {
                      const removedFile = files.find((f) => f.id === id);
                      if (removedFile) {
                        setResolvedConflictNames((prev) => {
                          const next = new Set(prev);
                          next.delete(removedFile.originalName);
                          return next;
                        });
                      }
                      setFileValidationErrors((prev) => {
                        if (!prev.has(id)) return prev;
                        const next = new Map(prev);
                        next.delete(id);
                        return next;
                      });
                      removeFile(id);
                    }}
                    onAddTag={addTag}
                    onRemoveTag={removeTag}
                    disabled={isUploading}
                    isDuplicate={
                      !resolvedConflictNames.has(file.originalName) &&
                      duplicateFileNames.has(file.originalName)
                    }
                    validationError={fileValidationErrors.get(file.id)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Recent uploads section -- visible only before any file interaction */}
          <AnimatePresence>
            {showRecentUploads && (
              <motion.div
                className="mt-6"
                initial={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0, overflow: "hidden" }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-primary">
                    Recent Uploads
                  </h3>
                  <span className="text-xs text-muted">
                    {Math.min(recentDocuments.length, 3)} files
                  </span>
                </div>
                <div className="space-y-2">
                  {recentDocuments.slice(0, 3).map((doc) => (
                    <button
                      key={`recent-${doc.id}`}
                      type="button"
                      onClick={() => handleRecentDocumentClick(doc.id)}
                      className="flex w-full items-center gap-3 rounded-lg bg-card px-3 py-2.5 hover:bg-card-hover transition-colors cursor-pointer text-left"
                    >
                      <FileIcon type={doc.type} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-primary truncate">
                          {doc.title}
                        </p>
                        <p className="text-xs text-muted">{doc.size}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Category assignment view */}
          {isAssigning && (
            <CategoryAssignmentView
              files={files}
              onSetFileCategoryId={setFileCategoryId}
              onSetFileExpirationDate={setFileExpirationDate}
              onBack={goBackToFileList}
              onUpload={startUpload}
              isUploading={isUploading}
            />
          )}
        </SideSheet.Body>

        <SideSheet.Footer>
          {!isAssigning && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              {hasFiles && (
                <Button
                  variant="primary"
                  onClick={handleContinue}
                  disabled={
                    isUploading ||
                    (!preSelectedAudit &&
                      store.selectedAuditIds.length === 0 &&
                      !store.explicitNoAudit)
                  }
                  className="flex items-center gap-2"
                >
                  Continue
                  <ArrowRight size={16} />
                  {!allFilesHaveCategory && (
                    <span className="ml-1 text-xs bg-destructive/20 text-destructive px-1.5 py-0.5 rounded-full">
                      {filesWithCategory}/{files.length}
                    </span>
                  )}
                </Button>
              )}
            </>
          )}
        </SideSheet.Footer>
      </SideSheet>

      {/* Duplicate conflict resolution modal (document-007) */}
      <DuplicateConflictModal
        open={showConflictModal}
        conflicts={unresolvedConflicts}
        nextAvailableNames={nextAvailableNames}
        onResolveAll={handleConflictsResolved}
        onCancel={handleConflictCancel}
      />
    </>
  );
}
